import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  fetchAccounts,
  fetchPlayers,
  fetchSettings,
  type AccountView,
  type PlayerView,
  type SignInSettingsView,
} from '../api/admin';
import { fetchScans, type ScanView } from '../api/catalog';
import { ca } from '../i18n/ca';
import { AccountsTab } from './admin/Accounts';
import { ActivityTab } from './admin/Activity';
import { CatalogTab } from './admin/CatalogScans';
import { CbaSettingsTab } from './admin/CbaSettings';
import { IntegrationsTab } from './admin/Integrations';
import { InvitationsTab } from './admin/Invitations';
import { PlayersTab } from './admin/Players';
import { RecipesTab } from './admin/Recipes';
import { ADMIN_TABS, ADMIN_TAB_ICONS, errorText, isAdminTab, type AdminTab } from './admin/shared';
import { useAuth } from './AuthContext';
import { useMeta } from './MetaContext';
import { Shell } from './Shell';

/**
 * The admin page (UI doc 5.11), one tab per job: accounts and roles, the players the game saw,
 * the integrations (doors, Discord, panel), the catalogue's scans, activity.
 * The active tab lives in `?tab=` so a reload or a shared link lands on it.
 */
export function Admin() {
  const { meta, reload: reloadMeta } = useMeta();
  const { me } = useAuth();
  const [search, setSearch] = useSearchParams();
  const tabParam = search.get('tab');
  const tab: AdminTab = isAdminTab(tabParam) ? tabParam : 'accounts';
  const [accounts, setAccounts] = useState<AccountView[]>([]);
  const [scans, setScans] = useState<ScanView[]>([]);
  const [players, setPlayers] = useState<PlayerView[]>([]);
  const [settings, setSettings] = useState<SignInSettingsView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    try {
      const [a, p, s] = await Promise.all([
        fetchAccounts(),
        fetchPlayers().catch(() => [] as PlayerView[]),
        fetchSettings().catch(() => null),
      ]);
      setAccounts(a);
      setPlayers(p);
      setSettings(s);
      setError(null);
    } catch (err) {
      setError(errorText(err));
    }
  }, []);

  useEffect(() => {
    if (!me.authenticated) return;
    void load();
    fetchScans(true)
      .then(setScans)
      .catch(() => setScans([]));
  }, [me.authenticated, load]);

  function selectTab(next: AdminTab) {
    setSearch(next === 'accounts' ? {} : { tab: next }, { replace: true });
    setNotice(null);
  }

  function replaceAccount(updated: AccountView) {
    setAccounts((list) => list.map((a) => (a.id === updated.id ? updated : a)));
  }

  const labels: Record<AdminTab, string> = ca.adminUi.tabs;
  const counts: Partial<Record<AdminTab, number>> = {
    accounts: accounts.length,
    players: players.length,
  };

  return (
    <Shell
      title={ca.admin}
      actions={
        <button
          type="button"
          className="primary"
          onClick={() => {
            selectTab('accounts');
            setCreating(true);
          }}
        >
          <i className="bi bi-person-plus" aria-hidden="true" /> {ca.newAccount}
        </button>
      }
    >
      <div className="admin-page">
        <nav className="tabs" role="tablist" aria-label={ca.admin}>
          {ADMIN_TABS.map((t) => (
            <button
              key={t}
              type="button"
              role="tab"
              aria-selected={tab === t}
              className={tab === t ? 'active' : ''}
              onClick={() => selectTab(t)}
            >
              <i className={`bi bi-${ADMIN_TAB_ICONS[t]}`} aria-hidden="true" />
              {labels[t]}
              {counts[t] ? <span className="chip">{counts[t]}</span> : null}
            </button>
          ))}
        </nav>
        {error && (
          <p className="notice bad" role="alert">
            {error}
          </p>
        )}
        {notice && (
          <p className="notice ok" role="status">
            {notice}
          </p>
        )}
        {tab === 'accounts' && (
          <AccountsTab
            accounts={accounts}
            onChange={replaceAccount}
            onCreated={(created) =>
              setAccounts((list) =>
                [...list, created].sort((a, b) => a.handle.localeCompare(b.handle)),
              )
            }
            onError={setError}
            onNotice={setNotice}
            creating={creating}
            onCloseCreate={() => setCreating(false)}
          />
        )}
        {tab === 'invitations' && <InvitationsTab onError={setError} onNotice={setNotice} />}
        {tab === 'players' && (
          <PlayersTab
            players={players}
            accounts={accounts}
            onPlayers={(list) => {
              setPlayers(list);
              void load();
            }}
            onError={setError}
          />
        )}
        {tab === 'integrations' && (
          <IntegrationsTab
            settings={settings}
            meta={meta}
            onSaved={(s) => {
              setSettings(s);
              void reloadMeta();
            }}
            onError={setError}
          />
        )}
        {tab === 'cba' && <CbaSettingsTab onError={setError} onNotice={setNotice} />}
        {tab === 'recipes' && <RecipesTab onError={setError} />}
        {tab === 'catalog' && <CatalogTab scans={scans} onScans={setScans} />}
        {tab === 'activity' && <ActivityTab accounts={accounts} onError={setError} />}
      </div>
    </Shell>
  );
}
